import { useCallback, useEffect, useState } from 'react'
import { FiSearch } from 'react-icons/fi'
import { Link } from 'react-router-dom'

import { PageNavigation } from '~/components/PageNavigation/index'
import { StudentCard } from '~/components/StudentCard'
import { TextInput } from '~/components/TextInput'
import { authenticationAtom } from '~/modules/authentication/data'
import { PersonalTrainerUser } from '~/modules/authentication/types'
import { studentsAtom } from '~/modules/students/data'
import { listStudents } from '~/modules/students/services'
import { StudentsQuery } from '~/modules/students/types'
import { PersonalTrainerSubscription } from '~/modules/subscriptions/types'
import { useAtom } from '~/observers/jotai'
import { api } from '~/utils/api'
import { debouncer } from '~/utils/debounce'

import { DemoCard } from './components/DemoCard'

let controller = new AbortController()
const debounce = debouncer()

export const PersonalTrainerHome: React.FC = () => {
  const [userData] = useAtom(authenticationAtom)
  const [loading, setloading] = useState<boolean>(true)
  const [showSubscriptionPrompt, setShowSubscriptionPromptsetloading] =
    useState<boolean>(false)
  const [data] = useAtom(studentsAtom)
  const [filter, setFilter] = useState<StudentsQuery>({
    page: 0,
  })

  const updateQuery = useCallback((newQuery: string) => {
    debounce(() => setFilter((state) => ({ ...state, name: newQuery })))
  }, [])

  const updatePage = useCallback((page: number) => {
    setFilter((state) => ({ ...state, page }))
  }, [])

  useEffect(() => {
    ;(async () => {
      controller = new AbortController()
      await listStudents(filter, controller)
      setloading(false)
    })()

    return () => {
      controller.abort()
    }
  }, [filter])

  useEffect(() => {
    const abortController = new AbortController()
    api
      .get<PersonalTrainerSubscription>('/personal-trainers/subscriptions', {
        signal: abortController.signal,
      })
      .then(({ data }) => {
        if (!data) {
          setShowSubscriptionPromptsetloading(true)
        } else if (data && new Date(data.termEnd) < new Date()) {
          setShowSubscriptionPromptsetloading(true)
        }
      })
      .catch(() => {
        /**/
      })

    return () => {
      abortController.abort()
    }
  }, [])

  return (
    <div className='flex flex-1 flex-col px-6 py-7 gap-4 overflow-y-auto'>
      <p>Olá, {userData?.user.name}</p>
      {showSubscriptionPrompt ? (
        <div className='flex flex-col gap-2 mx-auto mb-4 bg-gpa-blue-50 border-2 rounded-lg border-gpa-blue-400 px-3 py-2'>
          <h3 className='font-semibold text-xl'>
            Nenhuma assinatura ativa no momento!
          </h3>
          <p>
            Para continuar utilizando o GPA, é necessário assinar um plano de
            personal trainer. Você pode fazer isso clicando no botão abaixo.
          </p>
          <Link
            target='_blank'
            rel='noopener noreferrer'
            to='/ofertas'
            className='mt-2 bg-gpa-blue-400 hover:bg-gpa-blue-500 text-white font-semibold px-4 py-2 rounded-lg flex items-center justify-center'
          >
            Ver ofertas
          </Link>
        </div>
      ) : null}

      <div className='flex flex-wrap justify-between items-center p-3 gap-2 bg-white w-full rounded-2xl'>
        <h1 className='font-semibold text-2xl'>Alunos</h1>

        <div className='w-full md:w-auto'>
          <TextInput
            noError
            className=''
            onChange={(e) => updateQuery(e.target.value)}
            rightIconArea={[
              <FiSearch
                key='search-input'
                size={16}
                className='text-gpa-gray-500 group-focus-within:text-gpa-blue-500'
              />,
            ]}
          />
        </div>
      </div>
      {loading ? (
        <p className='text-center self-center'>Carregando...</p>
      ) : (
        <>
          {data.students.length ? (
            <div className='grid grid-cols-[repeat(auto-fit,_minmax(_245px,324px))] justify-center grid-flow-row w-full gap-4'>
              {data.students.slice(0, 10).map((student, index) => (
                <StudentCard key={`student-${index}`} student={student} />
              ))}
            </div>
          ) : (
            <>
              <p className='text-center self-center mt-12'>
                Nenhum registro encontrado.
              </p>
              <DemoCard />
            </>
          )}
        </>
      )}

      <div className='flex flex-col mx-auto mt-auto w-full'>
        <div className='ml-auto'>
          <PageNavigation
            currentPage={data.meta.page || 0}
            numPages={data.meta.totalPages || 0}
            onPageChange={(page) => updatePage(page)}
          />
        </div>
        <p className='text-center flex-1'>
          Alunos {data.students.length ?? 0}/
          {(userData?.user as PersonalTrainerUser)?.personalTrainerLicense?.[0]
            ?.finalAmount ?? 0}
        </p>
      </div>
    </div>
  )
}
